import { Image as AntImage, Tooltip } from 'antd';
import { previewImage } from 'antd/lib/upload/utils';
import classNames from 'classnames/bind';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import styled from 'styled-components';
import {
  BasicImagePreview,
  fileFormatNotAbleToPreview,
  localStorageWorkspacePath,
  nonImageFileToPreview,
} from 'components/organisms/image-preview';
import { RoutePaths } from 'constant';
import { useLocalStorageSession } from 'hooks/use-local-storage-session';
import { useTranslation } from 'hooks/use-translation';
import { logger } from 'services';
import { handleDownload } from 'utils/attachment';
import { extname, isImageUrlFromExtension, openFileUrl } from 'utils/files';
import styles from './file-thumbnail.module.css';

const ImageThumbnail = styled(AntImage)`
  height: 100%;
  width: 100%;
`;

type FileThumbnailProps = {
  className?: string;
  file?: File | Blob;
  fileName: string;
  id?: number;
  isUploading?: boolean;
  large?: boolean;
  url?: string;
  thumbUrl?: string;
  showCustomPreview?: boolean;
  imageWidth?: number;
  imageHeight?: number;
  onClick?: () => void;
  isBasicPreview?: boolean;
  isAnnotationStarted?: boolean;
};
const CustomPreviewProp = {
  visible: false,
};

export const FileThumbnail: FC<FileThumbnailProps> = ({
  className,
  file,
  fileName,
  id,
  isUploading,
  large,
  url,
  thumbUrl,
  imageWidth,
  imageHeight,
  onClick,
  showCustomPreview = true,
  isBasicPreview,
  isAnnotationStarted,
}) => {
  const history = useHistory();
  const [isImagePreviewVisible, setImagePreviewState] = useState(false);
  const [filePreview, setFilePreview] = useState('');
  const [fileExtension, setFileExtension] = useState('');
  const [isImage, setIsImage] = useState(false);
  const loadingAttachmentText = useTranslation(
    'attachments.upload_in_progress',
  );
  const downloadAttachmentErrorText = useTranslation(
    'attachments.download.error',
  );
  const { pathname } = useLocation();
  const { setItem } = useLocalStorageSession(
    localStorageWorkspacePath,
    pathname,
  );

  useEffect(() => {
    if (file && isImage) {
      previewImage(file)
        .then((base64Encode) => {
          setFilePreview(base64Encode);
        })
        .catch((error) => {
          /* TODO:: Fix this browser error.
           * NOTE:: https://developers.google.com/web/updates/2018/10/chrome-71-deps-rems#remove_urlcreateobjecturl_from_mediastream */
          logger.error('Unable to load file', { error, file });
        });
    } else if (extname(url || fileName) === 'pdf') {
      setFilePreview(url || fileName);
    }
  }, [file, fileName, isImage, url]);

  useEffect(() => {
    const fileExtenionValue = extname(url || fileName);

    if (fileExtenionValue) {
      setFileExtension(extname(url || fileName).toLowerCase());
    }
    setIsImage(isImageUrlFromExtension(url || fileName));
  }, [url, fileName]);

  const OtherFiles = () => <h6 className="h6">{fileExtension}</h6>;

  const File = () => {
    return isImage ? (
      <ImageThumbnail
        preview={false}
        alt={fileName}
        loading="lazy"
        src={filePreview}
        title={fileName}
      />
    ) : (
      <OtherFiles />
    );
  };

  const cx = classNames.bind(styles);
  /* eslint-disable @typescript-eslint/naming-convention */
  const itemClass = cx(styles.item, className, {
    item__other: !isImage,
    item__uploading: isUploading,
    item__pdf: fileExtension === 'pdf',
    item__csv: fileExtension === 'csv' || fileExtension === 'xlsx',
    item__ppt: fileExtension === 'pptx',
    item__docs: fileExtension === 'docx',
    item__large: large,
  });
  /* eslint-enable @typescript-eslint/naming-convention */

  const openFileInWorkspace = useCallback(() => {
    setItem();
    if (showCustomPreview && id) {
      history.push(
        RoutePaths.attachmentWorkspace.replaceAll(
          ':attachmentId',
          id.toString(),
        ),
      );
    }
  }, [history, id, setItem, showCustomPreview]);

  const onImageOnClick = useCallback(
    (e) => {
      onClick?.();
      e.stopPropagation();
      if (isBasicPreview || !isAnnotationStarted) {
        setImagePreviewState(true);
      } else {
        openFileInWorkspace();
      }
    },
    [isAnnotationStarted, isBasicPreview, onClick, openFileInWorkspace],
  );

  const onNonImageOnClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (fileFormatNotAbleToPreview.includes(fileExtension)) {
        openFileInWorkspace();
      } else if (nonImageFileToPreview.includes(fileExtension)) {
        if (isBasicPreview || !isAnnotationStarted) {
          setImagePreviewState(true);
        } else {
          openFileInWorkspace();
        }
      } else {
        openFileUrl(e, id, downloadAttachmentErrorText);
      }
    },
    [
      downloadAttachmentErrorText,
      fileExtension,
      id,
      isAnnotationStarted,
      isBasicPreview,
      openFileInWorkspace,
    ],
  );

  return (
    <>
      <Tooltip title={fileName}>
        <div
          className={itemClass}
          onClick={onClick}
          role="button"
          onKeyPress={onClick}
          tabIndex={0}
        >
          {url ? (
            <>
              {isImage ? (
                <ImageThumbnail
                  preview={showCustomPreview ? CustomPreviewProp : false}
                  alt={fileName}
                  loading="lazy"
                  src={url}
                  title={fileName}
                  width={imageWidth ?? undefined}
                  height={imageHeight ?? undefined}
                  onClick={onImageOnClick}
                  placeholder={
                    isImage ? (
                      <ImageThumbnail
                        preview={false}
                        src={thumbUrl}
                        alt={fileName}
                        loading="lazy"
                        title={fileName}
                      />
                    ) : (
                      <OtherFiles />
                    )
                  }
                />
              ) : (
                /* TODO:: try to provide url, so that user can right click and open the file(attachment). */
                // eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
                <a
                  rel="noreferrer noopener"
                  target="_blank"
                  onClick={onNonImageOnClick}
                >
                  <OtherFiles />
                </a>
              )}
            </>
          ) : (
            <span aria-label={loadingAttachmentText}>
              <File />
            </span>
          )}
        </div>
      </Tooltip>
      {showCustomPreview ? (
        <BasicImagePreview
          data={id}
          alt={fileName}
          fileName={fileName}
          loading="lazy"
          src={url}
          preview={isImagePreviewVisible}
          onClose={() => setImagePreviewState(false)}
          onDownload={() => handleDownload(id, downloadAttachmentErrorText)}
          isBasicPreview={isBasicPreview ?? false}
        />
      ) : null}
    </>
  );
};
